<template>
  <div
    class="element-card"
    :class="{'element-card--error': hasError, 'element-card--expanded': expanded}"
  >
    <div
      class="element-card-header"
      @click="toggle"
    >
      <div
        v-if="statsFields.length"
        class="element-card-header-stats"
        @click.stop
      >
        <div
          v-for="(field, index) in statsFields"
          :key="'stats-' + index"
          class="element-card-stat"
        >
          <span class="element-card-stat-label">{{ field.label }}</span>
          <card-section-element-card-field
            :column-config="field"
            :element="element"
            @elements-changed="$emit('elements-changed')"
          />
        </div>
      </div>

      <div
        class="element-card-header-title"
        @click.stop
      >
        <card-section-element-card-field
          v-for="(field, index) in titleFields"
          :key="'title-' + index"
          :column-config="field"
          :element="element"
          @elements-changed="$emit('elements-changed')"
        />
      </div>

      <div
        v-if="metaFields.length"
        class="element-card-header-meta"
        @click.stop
      >
        <div
          v-for="(field, index) in metaFields"
          :key="'meta-' + index"
          class="element-card-stat"
        >
          <span class="element-card-stat-label">{{ field.label }}</span>
          <card-section-element-card-field
            :column-config="field"
            :element="element"
            @elements-changed="$emit('elements-changed')"
          />
        </div>
      </div>

      <div
        v-if="actionFields.length"
        class="element-card-header-actions"
        @click.stop
      >
        <card-section-element-card-field
          v-for="(field, index) in actionFields"
          :key="'actions-' + index"
          :column-config="field"
          :element="element"
          @elements-changed="$emit('elements-changed')"
        />
      </div>

      <el-button
        class="element-card-chevron"
        type="text"
        :icon="expanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
        @click.stop="toggle"
      ></el-button>
    </div>

    <div
      v-show="expanded"
      class="element-card-body"
      :class="{'element-card-body--no-side': !sideFields.length}"
      :style="bodyGridStyle"
    >
      <div
        v-if="mainFields.length"
        class="element-card-body-main"
      >
        <div
          v-for="(field, index) in mainFields"
          :key="'main-' + index"
          class="element-card-box"
        >
          <div
            v-if="field.label"
            class="element-card-box-label"
          >
            {{ field.label }}
          </div>
          <card-section-element-card-field
            :column-config="field"
            :element="element"
            @elements-changed="$emit('elements-changed')"
          />
        </div>
      </div>

      <div
        v-for="(field, index) in sideFields"
        :key="'side-' + index"
        class="element-card-comment"
      >
        <div
          v-if="field.label"
          class="element-card-comment-header"
        >
          {{ field.label }}
        </div>
        <card-section-element-card-field
          :column-config="field"
          :element="element"
          @elements-changed="$emit('elements-changed')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CardSectionElementCardField from "@/components/cards/CardElementsBlock/CardSectionElementCardField.vue";

export default {
  name: "card-section-element-card",
  components: {CardSectionElementCardField},
  props: {
    element: {type: Object, required: true},
    headerFields: {type: Array, default: () => []},
    bodyFields: {type: Array, default: () => []},
    expandedByDefault: {type: Boolean, default: false},
    hasError: {type: Boolean, default: false},
  },
  data() {
    return {
      expanded: this.expandedByDefault,
    }
  },
  computed: {
    statsFields() {
      return this.fieldsByGroup(this.headerFields, 'stats');
    },
    titleFields() {
      const titled = this.fieldsByGroup(this.headerFields, 'title');
      const ungrouped = this.headerFields.filter((field) => !field.group);
      return titled.concat(ungrouped);
    },
    metaFields() {
      return this.fieldsByGroup(this.headerFields, 'meta');
    },
    actionFields() {
      return this.fieldsByGroup(this.headerFields, 'actions');
    },
    mainFields() {
      const main = this.fieldsByGroup(this.bodyFields, 'main');
      const ungrouped = this.bodyFields.filter((field) => !field.group);
      return main.concat(ungrouped);
    },
    sideFields() {
      return this.fieldsByGroup(this.bodyFields, 'side');
    },
    bodyGridStyle() {
      if (!this.sideFields.length) {
        return {};
      }
      return {
        gridTemplateColumns: '1.4fr repeat(' + this.sideFields.length + ', 1fr)',
      };
    },
  },
  methods: {
    fieldsByGroup(fields, group) {
      return fields.filter((field) => field.group === group);
    },
    toggle() {
      this.expanded = !this.expanded;
    },
  }
}
</script>

<style lang="scss">
@import "@/assets/css/project-variables";

.element-card {
  position: relative;
  border: 1px solid #e4e7ed;
  border-radius: $--border-radius-base;
  margin-bottom: 8px;
  background: #fff;
  // Левый акцент — сразу задаёт цветовой якорь
  border-left: 3px solid #e4e7ed;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &--expanded {
    border-left-color: $--color-primary;
    border-color: mix(white, $--color-primary, 65%);
    box-shadow: 0 2px 12px rgba($--color-primary, 0.10);
  }

  &--error {
    background-color: mix(white, $--color-danger, 95%);
    border-left-color: $--color-danger;
  }

  &-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    cursor: pointer;
    min-height: 48px;
    border-radius: $--border-radius-base;
    transition: background-color 0.12s ease;

    &:hover {
      background-color: #f9f8ff;
    }

    &-stats,
    &-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    &-title {
      flex: 1 1 auto;
      min-width: 0;
      font-weight: 500;

      .click-to-edit-div {
        display: block;
        width: 100%;
      }
    }

    &-actions {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }
  }

  // Разделитель между шапкой и телом
  &--expanded &-header {
    border-bottom: 1px solid #f0eeff;
    border-radius: $--border-radius-base $--border-radius-base 0 0;
  }

  &-stat {
    display: flex;
    align-items: center;
    gap: 5px;
    background: mix(white, $--color-primary, 93%);
    border: 1px solid mix(white, $--color-primary, 78%);
    border-radius: 6px;
    padding: 4px 10px;
    white-space: nowrap;

    &-label {
      color: mix(black, $--color-primary, 35%);
      font-size: 11px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }

  &-chevron {
    flex-shrink: 0;
    padding: 0;
    color: mix(white, $--color-primary, 45%);
  }

  &-body {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr;
    gap: 10px;
    padding: 12px;
    background: #fafafa;
    border-radius: 0 0 $--border-radius-base $--border-radius-base;

    &--no-side {
      grid-template-columns: 1fr;
    }

    &-main {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      align-content: start;

      .element-card-box:first-child {
        grid-column: 1 / -1;
      }
    }
  }

  &-box,
  &-comment {
    background: #fff;
    border: 1px solid #e8e8f0;
    border-radius: $--border-radius-base;
    padding: 8px 10px;
    min-height: 52px;
  }

  &-stat {
    .click-to-edit-div.click-to-edit-enabled {
      min-width: 88px;
    }
  }

  &-box {
    .click-to-edit-div {
      display: block;
      width: 100%;
      min-width: 0;
    }

    .click-to-edit-enabled {
      display: block;
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }

    .el-select {
      width: 100%;
    }
  }

  &-comment {
    min-height: 160px;

    .comment-preview {
      min-height: 120px;
    }
  }

  &-box-label,
  &-comment-header {
    color: mix(black, $--color-primary, 30%);
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 6px;
  }
}
</style>
